/**
 * HTTP client for the Worker API.
 *
 * One place knows how a request is authenticated, how the error envelope is
 * turned back into a typed error, and how a 204 is represented. Hooks never
 * touch `fetch` directly, so an API change is a change in one file.
 *
 * Authentication is ambient: the client holds no credential, the session
 * travels in an `HttpOnly` cookie, and `credentials: 'same-origin'` makes the
 * browser attach it. Because the Worker is served from the same origin, no CORS
 * configuration is involved. A 401 therefore means the cookie is gone or was
 * rejected, which is published as a session expiry rather than handled here.
 */

import { isApiErrorCode, type ApiErrorCode } from '@shared/errors'

import { notifySessionExpired } from './session-events'

export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number
  readonly details: Record<string, unknown>

  constructor(code: ApiErrorCode, status: number, message: string, details: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }

  /** Seconds to wait, when the server reported a rate limit. */
  get retryAfterSeconds(): number | null {
    const value = this.details.retryAfterSeconds
    return typeof value === 'number' ? value : null
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Locale hint sent as a query parameter, used for error messages. */
  locale?: string
  /**
   * Treats a 401 as an expected answer instead of a lost session.
   *
   * Used by the boot-time `/api/auth/me` probe, where an anonymous visitor is
   * the normal case, and by sign-out, which is already clearing the client.
   */
  tolerateUnauthorized?: boolean
}

/**
 * Fields of a failure envelope, read without trusting the code.
 *
 * The code is only usable when the shared catalog knows it, but the message and
 * the details are still shown to the user: an envelope this build cannot
 * classify must not lose them, or a newer Worker error would surface as a bare
 * English placeholder.
 */
interface ErrorEnvelope {
  /** Server code when this build knows it, otherwise `null`. */
  code: ApiErrorCode | null
  /** Server copy, empty when the body carried none. */
  message: string
  details: Record<string, unknown>
}

function readErrorEnvelope(value: unknown): ErrorEnvelope | null {
  if (typeof value !== 'object' || value === null) {
    return null
  }
  const error = (value as { error?: unknown }).error
  if (typeof error !== 'object' || error === null) {
    return null
  }
  const record = error as Record<string, unknown>
  const rawCode = record.code
  const message = typeof record.message === 'string' ? record.message : ''
  if (typeof rawCode !== 'string' && message.length === 0) {
    return null
  }
  const details = record.details
  return {
    code: typeof rawCode === 'string' && isApiErrorCode(rawCode) ? rawCode : null,
    message,
    details: typeof details === 'object' && details !== null && !Array.isArray(details)
      ? (details as Record<string, unknown>)
      : {},
  }
}

/**
 * Performs one API call.
 *
 * A non-2xx response becomes an `ApiError` carrying the server's code, so the UI
 * can react to `invite_required` or `rate_limited` without parsing text.
 */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const headers = new Headers()
  if (options.body !== undefined) {
    headers.set('content-type', 'application/json')
  }

  const url = options.locale
    ? `${path}${path.includes('?') ? '&' : '?'}locale=${encodeURIComponent(options.locale)}`
    : path

  const response = await fetch(url, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
    credentials: 'same-origin',
  })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  const payload: unknown = text.length > 0 ? safeJsonParse(text) : null

  if (!response.ok) {
    if (response.status === 401 && !options.tolerateUnauthorized) {
      // The cookie is gone or the session row expired: every cached answer
      // belongs to the previous identity, so the app listener drops them.
      notifySessionExpired()
    }
    const envelope = readErrorEnvelope(payload)
    if (envelope) {
      // An unknown code is kept as `internal_error` so the field stays typed,
      // but the server's own message and details travel on unchanged.
      throw new ApiError(
        envelope.code ?? 'internal_error',
        response.status,
        envelope.message,
        envelope.details,
      )
    }
    // Not an error envelope at all: a gateway HTML page, a truncated body, an
    // aborted stream. The message stays empty so the UI renders its own
    // localized `internal_error` copy instead of an English placeholder.
    throw new ApiError('internal_error', response.status, '')
  }

  return payload as T
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}
